import {
  FallIncident,
  RiskZone,
  TimeBasedRisk,
  PreventiveMeasure,
  EmergencyContact,
  FallDetectionSettings,
  FallPreventionProgress,
  Location,
  MovementSnapshot,
  FallSeverity,
  FallStatus,
  RiskLevel,
  TimeOfDay
} from '../types';

// Helper to generate random number within range
const randomInRange = (min: number, max: number) => 
  Math.floor(Math.random() * (max - min + 1)) + min;

// Helper function to generate random dates within a range
const randomDate = (start: Date, end: Date) => {
  return new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime()));
};

// Helper function to generate random enum values
function randomEnum<T extends { [key: string]: string }>(enumObj: T): T[keyof T] {
  const values = Object.values(enumObj);
  const randomValue = values[Math.floor(Math.random() * values.length)];
  return randomValue as T[keyof T];
}

// Helper function to generate random locations
const generateLocation = (): Location => {
  const rooms = ['Living Room', 'Bedroom', 'Bathroom', 'Kitchen', 'Hallway'];
  const zones = ['North', 'South', 'East', 'West', 'Central'];
  
  return {
    room: rooms[Math.floor(Math.random() * rooms.length)],
    zone: zones[Math.floor(Math.random() * zones.length)],
    coordinates: {
      x: Math.floor(Math.random() * 100),
      y: Math.floor(Math.random() * 100)
    }
  };
};

// Generate realistic movement snapshot
const generateMovementSnapshot = (): MovementSnapshot => ({
  gaitSpeed: 0.8 + Math.random() * 0.4, // 0.8-1.2 m/s
  balance: 70 + Math.random() * 30, // 70-100 score
  stepSymmetry: 0.85 + Math.random() * 0.15, // 0.85-1.0 ratio
  turnStability: 75 + Math.random() * 25 // 75-100 score
});

// Generate room-specific descriptions
const getRoomSpecificDescription = (room: string): string => {
  const descriptions: { [key: string]: string[] } = {
    Bathroom: [
      'Lost balance while stepping out of shower',
      'Slipped on wet floor near sink',
      'Lost balance during night-time bathroom visit',
      'Stumbled while reaching for towel'
    ],
    Bedroom: [
      'Lost balance while getting out of bed',
      'Tripped on rug near bedside',
      'Stumbled during night-time walking',
      'Lost balance while dressing'
    ],
    Kitchen: [
      'Slipped while reaching for cabinet',
      'Lost balance near counter',
      'Stumbled while carrying dishes',
      'Lost footing near refrigerator'
    ],
    'Living Room': [
      'Tripped on carpet edge',
      'Lost balance while sitting in chair',
      'Stumbled while walking to couch',
      'Lost balance reaching for remote'
    ],
    Hallway: [
      'Lost balance during walk',
      'Stumbled on way to bathroom',
      'Tripped while turning corner',
      'Lost footing near doorway'
    ]
  };

  const defaultDescriptions = [
    'Sudden loss of balance',
    'Momentary dizziness caused stumble',
    'Tripped while walking',
    'Lost footing during movement'
  ];

  const options = descriptions[room] || defaultDescriptions;
  return options[Math.floor(Math.random() * options.length)];
};

// Generate mock preventive measures
export const generatePreventiveMeasures = (): PreventiveMeasure[] => {
  const measures: PreventiveMeasure[] = [];
  const titles = [
    'Optimize furniture layout',
    'Add grab bars',
    'Improve lighting',
    'Non-slip mats',
    'Regular exercise program',
    'Emergency response system',
    'Regular assessments',
    'Floor maintenance'
  ];
  const descriptions = [
    'Rearrange furniture to create clear walking paths and reduce obstacles',
    'Install grab bars in bathrooms and hallways',
    'Upgrade lighting in dimly lit areas',
    'Place non-slip mats in bathrooms and kitchen',
    'Start daily balance and strength exercises',
    'Set up personal emergency response pendant',
    'Schedule regular mobility assessments',
    'Regular floor maintenance and cleaning'
  ];

  const statusEnum = { IMPLEMENTED: 'IMPLEMENTED', IN_PROGRESS: 'IN_PROGRESS', PLANNED: 'PLANNED' } as const;
  const priorityEnum = { HIGH: 'HIGH', MEDIUM: 'MEDIUM', LOW: 'LOW' } as const;

  for (let i = 0; i < 8; i++) {
    const status = randomEnum(statusEnum);

    measures.push({
      id: `measure-${i}`,
      title: titles[i],
      description: descriptions[i],
      status,
      priority: randomEnum(priorityEnum),
      implementationDate: status === 'IMPLEMENTED'
        ? new Date(Date.now() - Math.random() * 90 * 24 * 60 * 60 * 1000).toISOString()
        : undefined,
      targetDate: status !== 'IMPLEMENTED'
        ? new Date(Date.now() + Math.random() * 90 * 24 * 60 * 60 * 1000).toISOString()
        : undefined,
      assignedTo: ['Family Member', 'Caregiver'],
      notes: ['Initial assessment completed', 'Follow-up scheduled']
    });
  }

  return measures;
};

// Generate mock fall incidents
export const generateFallIncidents = (count: number = 2): FallIncident[] => {
  const incidents: FallIncident[] = [];
  const now = new Date();
  const ninetyDaysAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);

  const severityEnum = { HIGH: 'HIGH', MEDIUM: 'MEDIUM', LOW: 'LOW' } as const;
  const statusEnum = { RESOLVED: 'RESOLVED', UNDER_REVIEW: 'UNDER_REVIEW', PENDING: 'PENDING' } as const;

  const possibleActions = [
    'Emergency services called',
    'Family notified',
    'Caregiver alerted',
    'Medical assessment performed',
    'Fall prevention measures reviewed',
    'Care plan updated',
    'Environment assessed',
    'Additional monitoring implemented'
  ];

  // Create two specific incidents with realistic scenarios
  const incidents_data: Array<{
    date: Date;
    location: { room: string; zone: string };
    severity: FallSeverity;
    description: string;
    responseTime: number;
  }> = [
    {
      date: new Date(now.getTime() - 15 * 24 * 60 * 60 * 1000), // 15 days ago
      location: { room: 'Bathroom', zone: 'North' },
      severity: 'MEDIUM',
      description: 'Lost balance while stepping out of shower',
      responseTime: 45 // 45 seconds
    },
    {
      date: new Date(now.getTime() - 75 * 24 * 60 * 60 * 1000), // 75 days ago
      location: { room: 'Bedroom', zone: 'East' },
      severity: 'LOW',
      description: 'Lost balance while getting out of bed during night',
      responseTime: 30 // 30 seconds
    }
  ];

  incidents_data.forEach((data, i) => {
    // Determine action count based on severity
    let actionCount: number;
    switch (data.severity) {
      case 'HIGH':
        actionCount = 3;
        break;
      case 'MEDIUM':
        actionCount = 2;
        break;
      case 'LOW':
        actionCount = 1;
        break;
      default:
        actionCount = 1;
    }

    const selectedActions = [...possibleActions]
      .sort(() => Math.random() - 0.5)
      .slice(0, actionCount);

    incidents.push({
      id: `incident-${i}`,
      timestamp: data.date.toISOString(),
      location: {
        ...data.location,
        coordinates: {
          x: Math.floor(Math.random() * 100),
          y: Math.floor(Math.random() * 100)
        }
      },
      severity: data.severity,
      status: i === 0 ? 'UNDER_REVIEW' : 'RESOLVED',
      description: data.description,
      responseTime: data.responseTime,
      actions: selectedActions
    });
  });

  return incidents;
};

// Generate mock risk zones
export const generateRiskZones = (): RiskZone[] => {
  const zones: RiskZone[] = [];
  const riskFactors = [
    'Poor lighting',
    'Uneven surface',
    'Slippery floor',
    'Cluttered space',
    'Loose rugs',
    'High traffic area',
    'Steep incline',
    'Narrow passage'
  ];
  const recommendations = [
    'Install better lighting',
    'Level the surface',
    'Add non-slip mats',
    'Clear the area',
    'Secure or remove rugs',
    'Add handrails',
    'Widen the passage',
    'Regular maintenance'
  ];

  const riskLevelEnum = { HIGH: 'HIGH', MEDIUM: 'MEDIUM', LOW: 'LOW' } as const;

  for (let i = 0; i < 8; i++) {
    const lastAssessmentDate = new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000);
    const nextAssessmentDate = new Date(Date.now() + Math.random() * 30 * 24 * 60 * 60 * 1000);

    zones.push({
      id: `zone-${i}`,
      location: generateLocation(),
      riskLevel: randomEnum(riskLevelEnum),
      riskFactors: [
        riskFactors[Math.floor(Math.random() * riskFactors.length)],
        riskFactors[Math.floor(Math.random() * riskFactors.length)]
      ],
      recommendations: [
        recommendations[Math.floor(Math.random() * recommendations.length)],
        recommendations[Math.floor(Math.random() * recommendations.length)]
      ],
      lastAssessment: lastAssessmentDate.toISOString(),
      nextAssessment: nextAssessmentDate.toISOString()
    });
  }

  return zones;
};

// Generate mock time-based risks
export const generateTimeBasedRisks = (): TimeBasedRisk[] => {
  const risks: TimeBasedRisk[] = [];
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const commonActivities = [
    'Walking to bathroom',
    'Getting out of bed',
    'Preparing meals',
    'Light exercise',
    'Taking medication'
  ];
  const recommendations = [
    'Use nightlight',
    'Keep path clear',
    'Take breaks',
    'Stay hydrated',
    'Use assistive devices'
  ];

  hours.forEach(hour => {
    risks.push({
      timeOfDay: `${hour.toString().padStart(2, '0')}:00` as TimeBasedRisk['timeOfDay'],
      riskLevel: Math.floor(Math.random() * 100),
      incidents: Math.floor(Math.random() * 10),
      commonActivities: [
        commonActivities[Math.floor(Math.random() * commonActivities.length)],
        commonActivities[Math.floor(Math.random() * commonActivities.length)]
      ],
      recommendations: [
        recommendations[Math.floor(Math.random() * recommendations.length)],
        recommendations[Math.floor(Math.random() * recommendations.length)]
      ]
    });
  });

  return risks;
};

// Generate default fall detection settings
export const getDefaultFallDetectionSettings = (): FallDetectionSettings => {
  return {
    alertThreshold: 30,
    notificationUrgency: 'HIGH',
    enableSoundAlerts: true,
    enableMobileNotifications: true,
    monitoringSensitivity: 'MEDIUM',
    monitoringSchedule: '24_7',
    enableAutomatedResponse: true,
    enableDataLogging: true,
    enableVideoRecording: false,
    dataRetentionDays: 90,
    enableDataSharing: true,
    emergencyContacts: [
      {
        name: 'John Doe',
        phone: '555-0123',
        email: 'john.doe@example.com'
      },
      {
        name: 'Jane Smith',
        phone: '555-0124',
        email: 'jane.smith@example.com'
      }
    ]
  };
};

// Generate mock fall prevention progress
export const generateFallPreventionProgress = (): FallPreventionProgress => {
  return {
    safetyMeasuresProgress: 75, // More realistic progress percentage
    riskAssessmentProgress: 90,
    staffTrainingProgress: 85, // Renamed to "Family Education Progress" in the UI
    lastUpdated: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days ago
    nextReviewDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString() // 14 days from now
  };
}; 